import { Extension, type Editor, type Range } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import Suggestion, { type SuggestionProps } from '@tiptap/suggestion';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import {
  Code,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Paperclip,
  TextQuote,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { menuItemVariants } from '../menu/menu.tsx';
import type { RichTextEditorLabels } from './labels.ts';

export interface SlashItem {
  id: string;
  label: string;
  icon: LucideIcon;
  keywords: string[];
  run: (editor: Editor, range: Range) => void;
}

interface SlashStorage {
  items: SlashItem[];
}

const slashStorage = (editor: Editor): SlashStorage | undefined =>
  (editor.storage as unknown as Record<string, SlashStorage | undefined>).slashCommand;

/*
 * The editor is built once; what the menu offers depends on props that can
 * change later (uploads). The items live in the extension's storage — the
 * component writes them in an effect, the suggestion reads them on every
 * keystroke.
 */
export const setSlashItems = (editor: Editor, items: SlashItem[]) => {
  const storage = slashStorage(editor);
  if (storage) storage.items = items;
};

/* The same list the "/" menu shows, for the block handle's plus button. */
export const getSlashItems = (editor: Editor): SlashItem[] =>
  slashStorage(editor)?.items ?? [];

export const buildSlashItems = (
  labels: RichTextEditorLabels,
  uploads: { enabled: boolean; pick: (kind: 'image' | 'file') => void },
): SlashItem[] => {
  const items: SlashItem[] = [
    {
      id: 'heading1',
      label: labels.heading1,
      icon: Heading1,
      keywords: ['h1', 'kop', 'titel', 'heading', 'title'],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run(),
    },
    {
      id: 'heading2',
      label: labels.heading2,
      icon: Heading2,
      keywords: ['h2', 'kop', 'heading'],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run(),
    },
    {
      id: 'heading3',
      label: labels.heading3,
      icon: Heading3,
      keywords: ['h3', 'kop', 'heading'],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run(),
    },
    {
      id: 'bulletList',
      label: labels.bulletList,
      icon: List,
      keywords: ['lijst', 'opsomming', 'bullet', 'list', 'ul'],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleBulletList().run(),
    },
    {
      id: 'orderedList',
      label: labels.orderedList,
      icon: ListOrdered,
      keywords: ['genummerd', 'nummer', 'ordered', 'number', 'ol'],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
    },
    {
      id: 'taskList',
      label: labels.taskList,
      icon: ListChecks,
      keywords: ['checklist', 'taak', 'todo', 'task', 'checkbox'],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleTaskList().run(),
    },
    {
      id: 'quote',
      label: labels.quote,
      icon: TextQuote,
      keywords: ['citaat', 'quote', 'blockquote'],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
    },
    {
      id: 'codeBlock',
      label: labels.codeBlock,
      icon: Code,
      keywords: ['code', 'codeblok'],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
    },
    {
      id: 'divider',
      label: labels.divider,
      icon: Minus,
      keywords: ['lijn', 'scheiding', 'divider', 'hr', 'rule'],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
    },
  ];

  if (uploads.enabled) {
    items.push(
      {
        id: 'image',
        label: labels.image,
        icon: ImageIcon,
        keywords: ['afbeelding', 'foto', 'image', 'picture'],
        run: (editor, range) => {
          editor.chain().focus().deleteRange(range).run();
          uploads.pick('image');
        },
      },
      {
        id: 'file',
        label: labels.file,
        icon: Paperclip,
        keywords: ['bestand', 'bijlage', 'file', 'attachment'],
        run: (editor, range) => {
          editor.chain().focus().deleteRange(range).run();
          uploads.pick('file');
        },
      },
    );
  }

  return items;
};

const filterItems = (items: SlashItem[], query: string): SlashItem[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return items;
  return items.filter(
    (item) =>
      item.label.toLowerCase().includes(needle) ||
      item.keywords.some((keyword) => keyword.startsWith(needle)),
  );
};

export interface SlashMenuPopupProps {
  items: SlashItem[];
  /** Which item the keyboard is on; -1 while the mouse is driving. */
  selected: number;
  noResults: string;
  onPick: (item: SlashItem) => void;
  onHover: (index: number) => void;
}

/* Shared with the block handle's plus, so both menus are one thing. */
export function SlashMenuPopup({
  items,
  selected,
  noResults,
  onPick,
  onHover,
}: SlashMenuPopupProps) {
  return (
    <div
      data-slot='slash-menu'
      role='menu'
      className={cn(
        'max-h-76 min-w-52 overflow-y-auto rounded-md border border-line bg-surface p-1.5',
        'shadow-menu outline-none animate-pop',
      )}
    >
      {items.length === 0 && (
        <p className='px-2.75 py-2.25 text-sm font-semibold text-ink-muted'>
          {noResults}
        </p>
      )}
      {items.map((item, index) => (
        <button
          key={item.id}
          type='button'
          role='menuitem'
          data-highlighted={index === selected || undefined}
          className={menuItemVariants({ tone: 'default' })}
          /* Mousedown would move focus out of the editor before click lands. */
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onPick(item)}
          onMouseEnter={() => onHover(index)}
        >
          <item.icon aria-hidden='true' />
          {item.label}
        </button>
      ))}
    </div>
  );
}

export const position = (
  element: HTMLElement,
  clientRect: (() => DOMRect | null) | null | undefined,
) => {
  const rect = clientRect?.();
  if (!rect) return;
  /*
   * Out of flow before measuring: still in flow it is a full-width block, and
   * shift() would clamp a "too wide" popup to the viewport edge.
   */
  element.style.position = 'fixed';
  element.style.left = '0';
  element.style.top = '0';
  const virtual = { getBoundingClientRect: () => rect };
  void computePosition(virtual, element, {
    strategy: 'fixed',
    placement: 'bottom-start',
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  }).then(({ x, y }) => {
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};

export const createSlashCommand = (noResults: string) =>
  Extension.create<object, SlashStorage>({
    name: 'slashCommand',

    addStorage() {
      return { items: [] };
    },

    addProseMirrorPlugins() {
      const storage = this.storage;
      return [
        Suggestion<SlashItem>({
          editor: this.editor,
          char: '/',
          allowSpaces: false,
          allow: ({ state, range }) => {
            const $from = state.doc.resolve(range.from);
            return !$from.parent.type.spec.code;
          },
          items: ({ query }) => filterItems(storage.items, query),
          command: ({ editor, range, props }) => props.run(editor, range),
          render: () => {
            let component: ReactRenderer<unknown, SlashMenuPopupProps> | null = null;
            let latest: SuggestionProps<SlashItem> | null = null;
            let selected = 0;
            let dismissed = false;

            const rerender = () => {
              if (!component || !latest) return;
              component.updateProps({
                items: latest.items,
                selected,
                noResults,
                onPick: (item: SlashItem) => latest?.command(item),
                onHover: (index: number) => {
                  selected = index;
                  rerender();
                },
              });
            };

            const destroy = () => {
              component?.element.remove();
              component?.destroy();
              component = null;
            };

            return {
              onStart: (props) => {
                latest = props;
                selected = 0;
                dismissed = false;
                component = new ReactRenderer(SlashMenuPopup, {
                  editor: props.editor,
                  props: {
                    items: props.items,
                    selected,
                    noResults,
                    onPick: (item: SlashItem) => latest?.command(item),
                    onHover: (index: number) => {
                      selected = index;
                      rerender();
                    },
                  },
                });
                component.element.classList.add('z-50');
                document.body.appendChild(component.element);
                position(component.element as HTMLElement, props.clientRect);
              },
              onUpdate: (props) => {
                latest = props;
                if (dismissed) return;
                selected = Math.min(selected, Math.max(0, props.items.length - 1));
                rerender();
                if (component) {
                  position(component.element as HTMLElement, props.clientRect);
                }
              },
              onKeyDown: ({ event }) => {
                if (dismissed || !latest) return false;
                if (event.key === 'Escape') {
                  dismissed = true;
                  destroy();
                  return true;
                }
                const count = latest.items.length;
                if (count === 0) return false;
                if (event.key === 'ArrowDown') {
                  selected = (selected + 1) % count;
                  rerender();
                  return true;
                }
                if (event.key === 'ArrowUp') {
                  selected = (selected - 1 + count) % count;
                  rerender();
                  return true;
                }
                if (event.key === 'Enter') {
                  latest.command(latest.items[selected]);
                  return true;
                }
                return false;
              },
              onExit: destroy,
            };
          },
        }),
      ];
    },
  });
